import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { DatePicker } from "../components/ui/date-picker";
import {
  FileText, Link2, Plus, Trash2, Copy, MessageCircle, Mail,
  Stethoscope, IdCard, Receipt, Upload, ExternalLink, CheckCircle2,
  Circle, AlertCircle, FileDown, Pencil, ShieldCheck, UserCheck, History,
} from "lucide-react";
import { toast } from "sonner";
import { downloadFigcCartellinoPdf } from "../components/modulistica/figcCartellinoPdf";
import { downloadContestualePdf } from "../components/modulistica/contestualePdf";
import { downloadMedicalRequestPdf } from "../components/modulistica/medicalRequestPdf";
import { downloadTaxCertificatePdf } from "../components/modulistica/taxCertificatePdf";
import OTPSignatureDialog from "../components/signature/OTPSignatureDialog";

const STATUS_MED = {
  richiesto: { label: "Richiesto", cls: "bg-amber-100 text-amber-700", icon: Circle },
  prenotato: { label: "Prenotato", cls: "bg-sky-100 text-sky-700", icon: AlertCircle },
  completato: { label: "Completato", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  scaduto: { label: "Scaduto", cls: "bg-red-100 text-red-700", icon: AlertCircle },
};

const DOC_TYPES = {
  ci_fronte: "Carta Identità (fronte)",
  ci_retro: "Carta Identità (retro)",
  passaporto: "Passaporto",
  codice_fiscale: "Codice Fiscale / Tessera Sanitaria",
  foto_tessera: "Foto tessera",
  altro: "Altro",
};

export default function ModulisticaPage() {
  const { activeTeam, season, canEdit } = useTeams();
  const [players, setPlayers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [signaturesMap, setSignaturesMap] = useState({}); // `${type}:${ref}` -> latest signature
  const [signaturesVersion, setSignaturesVersion] = useState(0);

  const loadCommon = useCallback(async () => {
    if (!activeTeam) return;
    const [p, s] = await Promise.all([
      api.get(`/teams/${activeTeam.id}/players`),
      api.get(`/teams/${activeTeam.id}/staff`).catch(() => ({ data: [] })),
    ]);
    setPlayers(p.data);
    setStaff(s.data);
  }, [activeTeam]);

  const loadSignatures = useCallback(async () => {
    if (!activeTeam) return;
    try {
      const { data } = await api.get(`/teams/${activeTeam.id}/signatures`);
      const map = {};
      // signatures come ordered desc by signed_at on the backend; keep the
      // first (latest) one per (type, ref) key
      for (const s of data || []) {
        const key = `${s.document_type}:${s.document_ref}`;
        if (!map[key]) map[key] = s;
      }
      setSignaturesMap(map);
    } catch (_) {
      setSignaturesMap({});
    }
  }, [activeTeam]);

  useEffect(() => { loadCommon(); }, [loadCommon]);
  useEffect(() => { loadSignatures(); }, [loadSignatures, signaturesVersion]);

  const bumpSignatures = useCallback(() => {
    setSignaturesVersion((v) => v + 1);
  }, []);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;

  return (
    <div className="space-y-6" data-testid="modulistica-page">
      <div>
        <div className="label-eyebrow">Area</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Modulistica</h1>
        <p className="text-slate-500">Iscrizioni online, FIGC, visite mediche, certificati fiscali e documenti d&apos;identità</p>
      </div>

      <Tabs defaultValue="iscrizioni">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="iscrizioni" data-testid="tab-iscrizioni"><Link2 size={14} className="mr-1" /> Iscrizioni online</TabsTrigger>
          <TabsTrigger value="figc" data-testid="tab-figc"><FileText size={14} className="mr-1" /> Cartellini FIGC</TabsTrigger>
          <TabsTrigger value="medical" data-testid="tab-medical"><Stethoscope size={14} className="mr-1" /> Visite mediche</TabsTrigger>
          <TabsTrigger value="fiscal" data-testid="tab-fiscal"><Receipt size={14} className="mr-1" /> Certificati fiscali</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><IdCard size={14} className="mr-1" /> Documenti ID</TabsTrigger>
        </TabsList>

        <TabsContent value="iscrizioni" className="mt-4"><EnrollmentTab team={activeTeam} players={players} canEdit={canEdit} signaturesMap={signaturesMap} /></TabsContent>
        <TabsContent value="figc" className="mt-4"><FigcTab team={activeTeam} players={players} signaturesMap={signaturesMap} onSigned={bumpSignatures} /></TabsContent>
        <TabsContent value="medical" className="mt-4"><MedicalTab team={activeTeam} players={players} canEdit={canEdit} signaturesMap={signaturesMap} onSigned={bumpSignatures} /></TabsContent>
        <TabsContent value="fiscal" className="mt-4"><FiscalTab team={activeTeam} players={players} season={season} /></TabsContent>
        <TabsContent value="documents" className="mt-4"><DocumentsTab team={activeTeam} players={players} staff={staff} canEdit={canEdit} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================
// Tab: Iscrizioni online (invites + submissions)
// ============================================================
function EnrollmentTab({ team, players, canEdit, signaturesMap = {} }) {
  const [invites, setInvites] = useState([]);
  const [subs, setSubs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ family_name: "", contact_email: "", contact_phone: "", notes: "", prefill_player_id: "" });

  const load = useCallback(async () => {
    const [i, s] = await Promise.all([
      api.get(`/teams/${team.id}/enrollment-invites`).catch(() => ({ data: [] })),
      api.get(`/teams/${team.id}/enrollment-submissions`).catch(() => ({ data: [] })),
    ]);
    setInvites(i.data);
    setSubs(s.data);
  }, [team.id]);
  useEffect(() => { load(); }, [load]);

  const createInvite = async () => {
    if (!form.family_name && !form.contact_email && !form.contact_phone) {
      return toast.error("Inserisci almeno cognome famiglia, email o telefono");
    }
    const payload = { ...form, team_id: team.id, prefill_player_id: form.prefill_player_id || null };
    await api.post(`/enrollment-invites`, payload);
    toast.success("Invito creato");
    setOpen(false);
    setForm({ family_name: "", contact_email: "", contact_phone: "", notes: "", prefill_player_id: "" });
    load();
  };

  const buildPublicUrl = (token) => `${window.location.origin}/iscrizione/${token}`;

  const copyLink = async (token) => {
    try {
      await navigator.clipboard.writeText(buildPublicUrl(token));
      toast.success("Link copiato");
    } catch (e) {
      console.warn("Clipboard error:", e);
      toast.error("Errore copia");
    }
  };

  const shareWA = (inv) => {
    const url = buildPublicUrl(inv.token);
    const msg = `Ciao${inv.family_name ? " famiglia " + inv.family_name : ""}! Per completare l'iscrizione a ${team.name} stagione ${team.season || ""} clicca qui: ${url}\n\nGrazie!`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const shareEmail = (inv) => {
    const url = buildPublicUrl(inv.token);
    const subject = `Iscrizione ${team.name} - Stagione ${team.season || ""}`;
    const body = `Ciao${inv.family_name ? " famiglia " + inv.family_name : ""},\n\nPer completare l'iscrizione clicca qui:\n${url}\n\nGrazie!`;
    window.location.href = `mailto:${inv.contact_email || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const remove = async (inv) => {
    if (!window.confirm(`Eliminare l'invito di ${inv.family_name || inv.token}?`)) return;
    await api.delete(`/enrollment-invites/${inv.id}`);
    load();
  };

  const promote = async (sub) => {
    if (!window.confirm(`Promuovere ${sub.player_first_name} ${sub.player_last_name} a giocatore in anagrafica?`)) return;
    await api.post(`/enrollment-submissions/${sub.id}/promote`);
    toast.success("Giocatore aggiunto in anagrafica");
    load();
  };

  const downloadDossier = async (sub) => {
    try {
      // Fetch signatures linked to this enrollment
      const fetchSig = async (docType, docRef) => {
        try {
          const { data } = await api.get(`/signatures/by-ref`, { params: { document_type: docType, document_ref: docRef } });
          return data;
        } catch {
          return null;
        }
      };
      // The submission carries signature_id and privacy_signature_id pointing to records,
      // but easier: look up by token (document_ref) for both types.
      const enrollSig = await fetchSig("enrollment", sub.token);
      const privacySig = await fetchSig("privacy_release", sub.token);
      const { downloadDossierIscrizionePdf } = await import("../components/modulistica/dossierIscrizionePdf");
      await downloadDossierIscrizionePdf({
        team, submission: sub,
        signatures: { enrollment: enrollSig, privacy: privacySig },
      });
      toast.success("Dossier scaricato");
    } catch (e) {
      toast.error("Errore generazione dossier");
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-5 border-slate-200">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="font-heading font-semibold text-lg">Inviti per le famiglie</div>
            <p className="text-xs text-slate-500">Un link unico per ogni famiglia. Clic per copiare / inviare su WhatsApp / via email.</p>
          </div>
          {canEdit && (
            <Button onClick={() => setOpen(true)} className="bg-sky-500 hover:bg-sky-600" data-testid="new-invite-btn">
              <Plus size={16} className="mr-1" /> Nuovo invito
            </Button>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {invites.map((inv) => (
            <div key={inv.id} className="p-4 rounded-xl border border-slate-200 bg-slate-50/40" data-testid={`invite-${inv.id}`}>
              <div className="flex items-start gap-3">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
                  inv.status === "submitted" ? "bg-emerald-100 text-emerald-600"
                  : inv.status === "opened" ? "bg-sky-100 text-sky-600"
                  : "bg-amber-100 text-amber-600"
                }`}>
                  {inv.status === "submitted" ? <CheckCircle2 size={18} /> : inv.status === "opened" ? <AlertCircle size={18} /> : <Circle size={18} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{inv.family_name || "Famiglia"}</div>
                  <div className="text-xs text-slate-500">
                    {inv.contact_email || ""} {inv.contact_phone ? `· ${inv.contact_phone}` : ""}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider mt-1 font-bold text-slate-400">
                    {inv.status === "submitted" ? "Compilato" : inv.status === "opened" ? "Aperto" : "In attesa"}
                  </div>
                </div>
                {canEdit && (
                  <Button size="icon" variant="ghost" onClick={() => remove(inv)}>
                    <Trash2 size={14} className="text-red-500" />
                  </Button>
                )}
              </div>
              <div className="mt-3 flex gap-1 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => copyLink(inv.token)} data-testid={`copy-link-${inv.id}`}>
                  <Copy size={12} className="mr-1" /> Copia link
                </Button>
                <Button size="sm" variant="outline" onClick={() => shareWA(inv)}>
                  <MessageCircle size={12} className="mr-1" /> WhatsApp
                </Button>
                <Button size="sm" variant="outline" onClick={() => shareEmail(inv)} disabled={!inv.contact_email}>
                  <Mail size={12} className="mr-1" /> Email
                </Button>
                <a href={buildPublicUrl(inv.token)} target="_blank" rel="noreferrer" className="inline-flex items-center text-xs text-slate-500 hover:text-sky-600 px-2">
                  <ExternalLink size={12} className="mr-1" /> Anteprima
                </a>
              </div>
            </div>
          ))}
          {invites.length === 0 && (
            <div className="md:col-span-2 p-10 text-center text-slate-400 border border-dashed border-slate-200 rounded-xl">
              <Link2 size={28} className="mx-auto mb-2 text-slate-300" />
              Nessun invito creato. Premi &quot;Nuovo invito&quot; per generare il primo link.
            </div>
          )}
        </div>
      </Card>

      {/* Submissions */}
      <Card className="p-5 border-slate-200">
        <div className="font-heading font-semibold text-lg mb-1">Iscrizioni ricevute</div>
        <p className="text-xs text-slate-500 mb-4">Compilate dai genitori via link. Promuovile a giocatore per inserirle in anagrafica.</p>
        <div className="space-y-2">
          {subs.map((s) => {
            const enrollSig = signaturesMap[`enrollment:${s.token}`];
            const privacySig = signaturesMap[`privacy_release:${s.token}`];
            return (
            <div key={s.id} className="p-3 rounded-lg border border-slate-200 flex items-center gap-3 flex-wrap" data-testid={`submission-${s.id}`}>
              <UserCheck size={18} className="text-emerald-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{s.player_last_name} {s.player_first_name}</div>
                <div className="text-xs text-slate-500">
                  Nato il {s.player_birth_date} · Genitore: {s.parent1_first_name} {s.parent1_last_name} · {s.parent1_phone}
                </div>
                {(enrollSig || privacySig) && (
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    {enrollSig && (
                      <SignedAuditBadge
                        signature={enrollSig}
                        documentType="enrollment"
                        documentRef={s.token}
                      />
                    )}
                    {privacySig && (
                      <SignedAuditBadge
                        signature={privacySig}
                        documentType="privacy_release"
                        documentRef={s.token}
                      />
                    )}
                  </div>
                )}
              </div>
              <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${
                s.status === "promoted" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
              }`}>
                {s.status === "promoted" ? "In anagrafica" : "Da validare"}
              </span>
              {canEdit && s.status !== "promoted" && (
                <Button size="sm" onClick={() => promote(s)} className="bg-emerald-500 hover:bg-emerald-600">
                  <CheckCircle2 size={12} className="mr-1" /> Promuovi a giocatore
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => downloadDossier(s)} data-testid={`dossier-${s.id}`}>
                <FileDown size={12} className="mr-1" /> Dossier PDF
              </Button>
            </div>
          );})}
          {subs.length === 0 && (
            <p className="text-center text-sm text-slate-400 py-6">Nessuna iscrizione ricevuta.</p>
          )}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nuovo invito iscrizione</DialogTitle>
            <DialogDescription className="sr-only">Crea un link unico per una famiglia.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Cognome famiglia</Label>
              <Input value={form.family_name} onChange={(e) => setForm({ ...form, family_name: e.target.value })} placeholder="Rossi" data-testid="invite-family-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email contatto</Label>
                <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
              </div>
              <div>
                <Label>Telefono</Label>
                <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+39..." />
              </div>
            </div>
            <div>
              <Label>Rinnovo di (opzionale)</Label>
              <Select value={form.prefill_player_id || "__none__"} onValueChange={(v) => setForm({ ...form, prefill_player_id: v === "__none__" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nuovo giocatore —</SelectItem>
                  {players.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.last_name} {p.first_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400 mt-1">Se selezioni un giocatore, il modulo arriverà pre-compilato.</p>
            </div>
            <div>
              <Label>Note interne</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={createInvite} className="bg-sky-500 hover:bg-sky-600" data-testid="save-invite-btn">Crea invito</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// Tab: Cartellini FIGC
// ============================================================
function SignedAuditBadge({ signature, documentType, documentRef }) {
  if (!signature) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] text-slate-400 italic"
        data-testid={`audit-pending-${documentRef}`}
      >
        <Circle size={10} /> Non firmato
      </span>
    );
  }
  let dateLabel = "";
  try {
    const d = new Date(signature.signed_at);
    dateLabel = d.toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    dateLabel = signature.signed_at;
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px]">
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium"
        title={`Firmato da ${signature.signer_email || ""}`}
        data-testid={`audit-signed-${documentRef}`}
      >
        <CheckCircle2 size={10} />
        {dateLabel}
      </span>
      <Link
        to={`/firme?type=${encodeURIComponent(
          documentType
        )}&ref=${encodeURIComponent(documentRef)}`}
        className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-700 hover:underline font-medium"
        data-testid={`audit-link-${documentRef}`}
        title="Vedi log audit della firma"
      >
        <History size={10} /> Vedi audit
      </Link>
    </span>
  );
}

function FigcTab({ team, players, signaturesMap = {}, onSigned }) {
  const missingInfo = !team.legal_name || !team.figc_affiliation_code || !team.fiscal_code;
  const [signOpen, setSignOpen] = useState(false);
  const [signTarget, setSignTarget] = useState(null); // player

  const handleSignAndDownload = (player) => {
    setSignTarget(player);
    setSignOpen(true);
  };

  const handleSkipSign = (player) => {
    downloadFigcCartellinoPdf({ team, player, parentName: player.parent_name });
  };

  return (
    <div className="space-y-4">
      {missingInfo && (
        <Card className="p-4 border-amber-200 bg-amber-50 text-amber-800 text-sm flex gap-2 items-start">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <div>
            <strong>Dati società incompleti.</strong> Vai su &quot;Modifica squadra&quot; e compila Denominazione, Matricola FIGC e Codice Fiscale per generare cartellini conformi.
          </div>
        </Card>
      )}
      <Card className="p-0 overflow-hidden border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Giocatore</th>
              <th className="text-left px-4 py-2">Data nascita</th>
              <th className="text-left px-4 py-2">CF</th>
              <th className="text-left px-4 py-2">Firma</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {players.map((p) => (
              <tr key={p.id} className="border-t border-slate-100" data-testid={`figc-row-${p.id}`}>
                <td className="px-4 py-2 font-medium">{p.last_name} {p.first_name}</td>
                <td className="px-4 py-2 text-slate-600">{p.birth_date || "—"}</td>
                <td className="px-4 py-2 text-slate-500 font-mono text-xs">{p.fiscal_code || "—"}</td>
                <td className="px-4 py-2">
                  <SignedAuditBadge
                    signature={signaturesMap[`figc_cartellino:${p.id}`]}
                    documentType="figc_cartellino"
                    documentRef={p.id}
                  />
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSkipSign(p)}
                    data-testid={`figc-pdf-${p.id}`}
                  >
                    <FileDown size={12} className="mr-1" /> PDF
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-2"
                    onClick={() => downloadContestualePdf({ team, player: p })}
                    data-testid={`figc-contestuale-${p.id}`}
                    title="Modulo contestuale (Padre + Madre)"
                  >
                    <FileDown size={12} className="mr-1" /> Contestuale
                  </Button>
                  <Button
                    size="sm"
                    className="ml-2 bg-sky-500 hover:bg-sky-600 text-white"
                    onClick={() => handleSignAndDownload(p)}
                    data-testid={`figc-sign-${p.id}`}
                    disabled={!p.parent_email}
                    title={p.parent_email ? `Invia firma a ${p.parent_email}` : "Email genitore mancante"}
                  >
                    <ShieldCheck size={12} className="mr-1" /> Firma & scarica
                  </Button>
                </td>
              </tr>
            ))}
            {players.length === 0 && (
              <tr><td className="px-4 py-6 text-center text-slate-400" colSpan={5}>Nessun giocatore in anagrafica.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {signTarget && (
        <OTPSignatureDialog
          open={signOpen}
          onOpenChange={setSignOpen}
          documentType="figc_cartellino"
          documentRef={signTarget.id}
          teamId={team.id}
          defaultEmail={signTarget.parent_email || ""}
          defaultName={signTarget.parent_name || ""}
          signerRoleLabel="Genitore / Tutore"
          onSigned={(sig) => {
            downloadFigcCartellinoPdf({
              team, player: signTarget, parentName: signTarget.parent_name, signature: sig,
            });
            onSigned?.();
          }}
          title={`Firma cartellino FIGC · ${signTarget.last_name} ${signTarget.first_name}`}
          description={`Il genitore (${signTarget.parent_email || "email mancante"}) riceverà un codice OTP via email per firmare il cartellino. La firma viene registrata con timestamp e impressa nel PDF.`}
        />
      )}
    </div>
  );
}

// ============================================================
// Tab: Visite mediche
// ============================================================
function MedicalTab({ team, players, canEdit, signaturesMap = {}, onSigned }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    player_id: "", visit_type: "agonistica", appointment_date: "", appointment_place: "",
    doctor_name: "", certificate_expiry: "", status: "richiesto", notes: "",
  });
  const [signOpen, setSignOpen] = useState(false);
  const [signTarget, setSignTarget] = useState(null); // { request, player }

  const load = useCallback(async () => {
    const r = await api.get(`/teams/${team.id}/medical-requests`).catch(() => ({ data: [] }));
    setItems(r.data);
  }, [team.id]);
  useEffect(() => { load(); }, [load]);

  const playerMap = Object.fromEntries(players.map((p) => [p.id, p]));

  const expiring = useMemo(() => {
    const today = new Date();
    const in30days = new Date(); in30days.setDate(today.getDate() + 30);
    return items.filter((i) => {
      if (!i.certificate_expiry || i.status !== "completato") return false;
      const e = new Date(i.certificate_expiry);
      return e <= in30days;
    });
  }, [items]);

  const submit = async () => {
    if (!form.player_id) return toast.error("Seleziona un giocatore");
    const payload = { ...form, team_id: team.id };
    if (editingId) await api.put(`/medical-requests/${editingId}`, payload);
    else await api.post(`/medical-requests`, payload);
    toast.success("Salvato");
    setOpen(false);
    setEditingId(null);
    load();
  };

  const handleNew = () => {
    setForm({ player_id: "", visit_type: "agonistica", appointment_date: "", appointment_place: "", doctor_name: "", certificate_expiry: "", status: "richiesto", notes: "" });
    setEditingId(null);
    setOpen(true);
  };
  const handleEdit = (m) => {
    setForm({
      player_id: m.player_id || "",
      visit_type: m.visit_type || "agonistica",
      appointment_date: m.appointment_date || "",
      appointment_place: m.appointment_place || "",
      doctor_name: m.doctor_name || "",
      certificate_expiry: m.certificate_expiry || "",
      status: m.status || "richiesto",
      notes: m.notes || "",
    });
    setEditingId(m.id);
    setOpen(true);
  };
  const remove = async (m) => {
    if (!window.confirm("Eliminare la richiesta?")) return;
    await api.delete(`/medical-requests/${m.id}`);
    load();
  };

  return (
    <div className="space-y-4">
      {expiring.length > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50 text-amber-800 text-sm flex gap-2 items-start">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <div>
            <strong>{expiring.length} certificat{expiring.length === 1 ? "o" : "i"} in scadenza nei prossimi 30 giorni</strong>: {expiring.map((e) => `${playerMap[e.player_id]?.last_name || ""}`).filter(Boolean).join(", ")}
          </div>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{items.length} richieste registrate</p>
        {canEdit && (
          <Button size="sm" onClick={handleNew} className="bg-sky-500 hover:bg-sky-600" data-testid="new-medical-btn">
            <Plus size={14} className="mr-1" /> Nuova richiesta
          </Button>
        )}
      </div>

      <Card className="p-0 overflow-hidden border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Giocatore</th>
              <th className="text-left px-4 py-2">Tipo</th>
              <th className="text-left px-4 py-2">Appuntamento</th>
              <th className="text-left px-4 py-2">Scadenza cert.</th>
              <th className="text-center px-4 py-2">Stato</th>
              <th className="text-left px-4 py-2">Firma</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => {
              const p = playerMap[m.player_id];
              const st = STATUS_MED[m.status] || STATUS_MED.richiesto;
              const StIcon = st.icon;
              return (
                <tr key={m.id} className="border-t border-slate-100" data-testid={`medical-${m.id}`}>
                  <td className="px-4 py-2 font-medium">{p ? `${p.last_name} ${p.first_name}` : "—"}</td>
                  <td className="px-4 py-2 text-slate-600 capitalize">{m.visit_type?.replace("_", " ")}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    {m.appointment_date ? `${m.appointment_date}${m.appointment_place ? ` · ${m.appointment_place}` : ""}` : "—"}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">{m.certificate_expiry || "—"}</td>
                  <td className="px-4 py-2 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${st.cls}`}>
                      <StIcon size={11} /> {st.label}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <SignedAuditBadge
                      signature={signaturesMap[`medical_request:${m.id}`]}
                      documentType="medical_request"
                      documentRef={m.id}
                    />
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => downloadMedicalRequestPdf({ team, player: p, request: m })} data-testid={`medical-pdf-${m.id}`}>
                      <FileDown size={12} className="mr-1" /> PDF
                    </Button>
                    <Button
                      size="sm"
                      className="ml-2 bg-sky-500 hover:bg-sky-600 text-white"
                      onClick={() => { setSignTarget({ request: m, player: p }); setSignOpen(true); }}
                      data-testid={`medical-sign-${m.id}`}
                      disabled={!p?.parent_email}
                      title={p?.parent_email ? `Invia firma a ${p.parent_email}` : "Email genitore mancante"}
                    >
                      <ShieldCheck size={12} className="mr-1" /> Firma & PDF
                    </Button>
                    {canEdit && (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => handleEdit(m)}><Pencil size={13} /></Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(m)}><Trash2 size={13} className="text-red-500" /></Button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td className="px-4 py-6 text-center text-slate-400" colSpan={7}>Nessuna richiesta.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica richiesta" : "Nuova richiesta visita"}</DialogTitle>
            <DialogDescription className="sr-only">Registra richiesta visita medica.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Giocatore</Label>
              <Select value={form.player_id} onValueChange={(v) => setForm({ ...form, player_id: v })}>
                <SelectTrigger data-testid="medical-player"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  {players.map((p) => (<SelectItem key={p.id} value={p.id}>{p.last_name} {p.first_name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo visita</Label>
                <Select value={form.visit_type} onValueChange={(v) => setForm({ ...form, visit_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agonistica">Agonistica</SelectItem>
                    <SelectItem value="non_agonistica">Non agonistica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Stato</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_MED).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data appuntamento</Label>
                <DatePicker value={form.appointment_date} onChange={(v) => setForm({ ...form, appointment_date: v })} />
              </div>
              <div>
                <Label>Scadenza certificato</Label>
                <DatePicker value={form.certificate_expiry} onChange={(v) => setForm({ ...form, certificate_expiry: v })} />
              </div>
            </div>
            <div>
              <Label>Luogo / Ambulatorio</Label>
              <Input value={form.appointment_place} onChange={(e) => setForm({ ...form, appointment_place: e.target.value })} />
            </div>
            <div>
              <Label>Medico</Label>
              <Input value={form.doctor_name} onChange={(e) => setForm({ ...form, doctor_name: e.target.value })} placeholder="Dr. Mario Bianchi" />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={submit} className="bg-sky-500 hover:bg-sky-600" data-testid="save-medical-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {signTarget && (
        <OTPSignatureDialog
          open={signOpen}
          onOpenChange={setSignOpen}
          documentType="medical_request"
          documentRef={signTarget.request.id}
          teamId={team.id}
          defaultEmail={signTarget.player?.parent_email || ""}
          defaultName={signTarget.player?.parent_name || ""}
          signerRoleLabel="Genitore / Tutore"
          onSigned={(sig) => {
            downloadMedicalRequestPdf({
              team, player: signTarget.player, request: signTarget.request, signature: sig,
            });
            onSigned?.();
          }}
          title={`Firma richiesta visita · ${signTarget.player?.last_name} ${signTarget.player?.first_name}`}
          description={`Il genitore (${signTarget.player?.parent_email || "email mancante"}) riceverà un codice OTP via email. La firma viene impressa nel PDF.`}
        />
      )}
    </div>
  );
}

// ============================================================
// Tab: Certificati fiscali
// ============================================================
function FiscalTab({ team, players, season }) {
  const [loadingId, setLoadingId] = useState(null);

  const generate = async (p) => {
    setLoadingId(p.id);
    try {
      const { data } = await api.get(`/teams/${team.id}/tax-certificate-data`, {
        params: { player_id: p.id, season: season || undefined },
      });
      await downloadTaxCertificatePdf({ data, recipient: p.parent_name });
      toast.success("Certificato generato");
    } catch (e) {
      console.error(e);
      toast.error("Errore generazione");
    }
    setLoadingId(null);
  };

  return (
    <div className="space-y-3">
      <Card className="p-4 border-sky-100 bg-sky-50/40 text-sm flex gap-2 items-start">
        <ShieldCheck size={16} className="text-sky-600 shrink-0 mt-0.5" />
        <div>
          <strong>Certificato art. 15 TUIR</strong> — Detrazione 19% per spese sportive dilettantistiche di figli 5-18 anni, max € 1.000/anno per figlio. Il PDF include i versamenti tracciati nelle Quote (Amministrazione + Tornei) per la stagione <strong>{season || "—"}</strong>.
        </div>
      </Card>
      <Card className="p-0 overflow-hidden border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Giocatore</th>
              <th className="text-left px-4 py-2">Genitore destinatario</th>
              <th className="text-left px-4 py-2">Email</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {players.map((p) => (
              <tr key={p.id} className="border-t border-slate-100" data-testid={`fiscal-row-${p.id}`}>
                <td className="px-4 py-2 font-medium">{p.last_name} {p.first_name}</td>
                <td className="px-4 py-2 text-slate-600">{p.parent_name || "—"}</td>
                <td className="px-4 py-2 text-xs text-slate-500">{p.parent_email || "—"}</td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="outline" disabled={loadingId === p.id} onClick={() => generate(p)} data-testid={`fiscal-pdf-${p.id}`}>
                    <FileDown size={12} className="mr-1" /> {loadingId === p.id ? "..." : "Genera certificato"}
                  </Button>
                </td>
              </tr>
            ))}
            {players.length === 0 && (
              <tr><td className="px-4 py-6 text-center text-slate-400" colSpan={4}>Nessun giocatore.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ============================================================
// Tab: Documenti d'identità
// ============================================================
function DocumentsTab({ team, players, staff, canEdit }) {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject_type: "player", subject_id: "", doc_type: "ci_fronte", url: "", filename: "", notes: "" });
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/teams/${team.id}/id-documents`).catch(() => ({ data: [] }));
    setDocs(r.data);
  }, [team.id]);
  useEffect(() => { load(); }, [load]);

  const subjectMap = useMemo(() => {
    const m = {};
    players.forEach((p) => { m[`player:${p.id}`] = `${p.last_name} ${p.first_name}`; });
    staff.forEach((s) => { m[`staff:${s.id}`] = `${s.last_name} ${s.first_name} (staff)`; });
    return m;
  }, [players, staff]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("File troppo grande (max 5MB)");
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(`/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, url: data.url, filename: file.name }));
      toast.success("Caricato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore upload");
    }
    setUploading(false);
  };

  const submit = async () => {
    if (!form.subject_id) return toast.error("Seleziona persona");
    if (!form.url) return toast.error("Carica il file");
    await api.post(`/id-documents`, { ...form, team_id: team.id });
    toast.success("Documento salvato");
    setOpen(false);
    setForm({ subject_type: "player", subject_id: "", doc_type: "ci_fronte", url: "", filename: "", notes: "" });
    load();
  };

  const remove = async (d) => {
    if (!window.confirm("Eliminare il documento?")) return;
    await api.delete(`/id-documents/${d.id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 border-slate-200 bg-slate-50/40 text-sm flex gap-2 items-start">
        <IdCard size={16} className="text-slate-500 shrink-0 mt-0.5" />
        <div>
          Documenti visibili solo a Mister e Dirigenti. <strong>I genitori non hanno accesso a questa sezione.</strong>
        </div>
      </Card>

      <div className="flex justify-end">
        {canEdit && (
          <Button onClick={() => setOpen(true)} className="bg-sky-500 hover:bg-sky-600" data-testid="new-document-btn">
            <Upload size={14} className="mr-1" /> Carica documento
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {docs.map((d) => (
          <Card key={d.id} className="p-4 border-slate-200" data-testid={`doc-${d.id}`}>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center shrink-0">
                <IdCard size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{subjectMap[`${d.subject_type}:${d.subject_id}`] || "—"}</div>
                <div className="text-xs text-slate-500">{DOC_TYPES[d.doc_type] || d.doc_type}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{d.uploaded_at?.slice(0, 10)}</div>
              </div>
              {canEdit && (
                <Button size="icon" variant="ghost" onClick={() => remove(d)}><Trash2 size={13} className="text-red-500" /></Button>
              )}
            </div>
            <a href={d.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center text-xs text-sky-600 hover:text-sky-700">
              <ExternalLink size={12} className="mr-1" /> Apri documento
            </a>
            {d.notes && <p className="mt-1 text-[11px] text-slate-500">{d.notes}</p>}
          </Card>
        ))}
        {docs.length === 0 && (
          <Card className="p-10 col-span-full text-center text-slate-400 border-dashed">
            <IdCard size={28} className="mx-auto mb-2 text-slate-300" />
            Nessun documento caricato.
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Carica documento d&apos;identità</DialogTitle>
            <DialogDescription className="sr-only">Carica un documento d&apos;identità di un giocatore o staff.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Persona di tipo</Label>
                <Select value={form.subject_type} onValueChange={(v) => setForm({ ...form, subject_type: v, subject_id: "" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="player">Giocatore</SelectItem>
                    <SelectItem value="staff">Staff/Dirigente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo documento</Label>
                <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(DOC_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{form.subject_type === "player" ? "Giocatore" : "Staff"}</Label>
              <Select value={form.subject_id} onValueChange={(v) => setForm({ ...form, subject_id: v })}>
                <SelectTrigger data-testid="doc-subject"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  {(form.subject_type === "player" ? players : staff).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.last_name} {p.first_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>File (JPG/PNG/WEBP - max 5MB)</Label>
              <Input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => handleUpload(e.target.files?.[0])} disabled={uploading} data-testid="doc-file" />
              {form.filename && <p className="text-xs text-emerald-600 mt-1">✓ {form.filename}</p>}
            </div>
            <div>
              <Label>Note</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={submit} disabled={uploading || !form.url} className="bg-sky-500 hover:bg-sky-600" data-testid="save-doc-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
